import { useEffect, useState } from "react";
import { Phone, WhatsappLogo, UserCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatPhoneDisplay } from "@/utils/phoneFormat";

// Single source of truth for the Contact / Management card used on Home & About.
// Card intentionally hides the WhatsApp button when `showWhatsapp` is false
// (GM privacy rule) OR when the contact object has no whatsapp number.
function ContactCard({ contact, title, roleLabel, showWhatsapp, testId }) {
  if (!contact || !contact.phone) return null;
  const displayPhone = formatPhoneDisplay(contact.phone);
  const waNumber = (contact.whatsapp || "").replace(/\D+/g, "");
  const telHref = `tel:${contact.phone.startsWith("+") ? contact.phone : "+" + contact.phone.replace(/\D+/g, "")}`;
  const waHref = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent("السلام عليكم، بخصوص خدمات مراسم الزوار")}`
    : null;

  return (
    <div
      data-testid={testId}
      className="fadeup relative rounded-2xl border border-primary/30 bg-gradient-to-br from-[#0F2148] to-[#172956] p-5 md:p-6 hover:border-primary/70 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.4)] transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0">
          <UserCircle size={28} weight="duotone" className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-primary/80 text-[11px] font-semibold uppercase tracking-wider mb-0.5">
            {roleLabel}
          </div>
          <div className="text-white font-bold text-lg leading-tight">{contact.name || title}</div>
          <div className="text-white/70 text-sm mt-1 num" dir="ltr">
            {displayPhone}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <a
          href={telHref}
          data-testid={`${testId}-call`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.7)] transition-all"
        >
          <Phone size={16} weight="fill" /> اتصال
        </a>
        {showWhatsapp && waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-whatsapp`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-bold hover:bg-[#1EBE5D] hover:shadow-[0_10px_30px_-10px_rgba(37,211,102,0.6)] transition-all"
          >
            <WhatsappLogo size={16} weight="fill" /> واتساب
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Shared "تواصل مع الإدارة" section — one component, two pages (Home + About).
 * Fetches /api/public/contacts and renders the GM (call-only) + Booking Manager
 * (call + WhatsApp) cards side-by-side on desktop, stacked on mobile.
 *
 * Props:
 *   - title       (string)  section heading (default: "تواصل معنا")
 *   - subtitle    (string)  optional supporting line
 *   - testId      (string)  section data-testid (default: "admin-contacts")
 *   - contactsProp (object) OPTIONAL — pre-fetched contacts to skip the request
 */
export default function AdminContactsSection({
  title = "تواصل معنا",
  subtitle = "فريقنا في خدمتك على مدار الساعة",
  testId = "admin-contacts",
  contactsProp = null,
}) {
  const [contacts, setContacts] = useState(
    contactsProp || { general_manager: null, booking_manager: null },
  );

  useEffect(() => {
    if (contactsProp) return; // parent already provided the data
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/contacts");
        if (!cancelled && data) setContacts(data);
      } catch {
        /* silent — section is optional and non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contactsProp]);

  if (!contacts?.general_manager && !contacts?.booking_manager) return null;

  return (
    <section data-testid={testId}>
      {title !== null && (
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary">{title}</h2>
            {subtitle && <p className="text-white/60 text-sm mt-1">{subtitle}</p>}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <ContactCard
          contact={contacts.general_manager}
          title="المدير العام"
          roleLabel="المدير العام"
          showWhatsapp={true}
          testId="contact-gm"
        />
        <ContactCard
          contact={contacts.booking_manager}
          title="مدير الحجوزات"
          roleLabel="مدير الحجوزات"
          showWhatsapp={true}
          testId="contact-bm"
        />
      </div>
    </section>
  );
}

export { ContactCard };
